import React from "react";
import RenderHtml from "../../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import PartsTable from "../../../../Documents/components/Tables/PartsTable";
import MoveSubstepNode from "./MoveSubstepNode";
import MoveChildNode from "./MoveChildNode";
import { calculateGlobalNodeNumberByType, compareSiblingOrders } from "./utils";

/**
 * MoveStepNode Component
 * Renders a step for move position selection
 */
const MoveStepNode = ({
  step,
  stepIndex,
  globalStepNumber,
  sectionIndex,
  sectionId,
  preloadedData,
  checkboxState,
  moveNodeType,
  checkboxLabelColor,
  customOnNavigate,
  shouldDisableStepCheckbox,
  shouldShowStepCheckbox,
  shouldDisableSubstepCheckbox,
  shouldShowSubstepCheckbox,
  shouldDisableChildCheckbox,
  shouldShowChildCheckbox,
}) => {
  const stepHeading = `Step ${globalStepNumber}`;

  // Determine checkbox state
  const isCurrent =
    checkboxState?.currentNodeId === step.tree_node_id &&
    checkboxState?.currentNodeType === "step";
  const isSourceStep =
    checkboxState?.sourceNodeId === step.tree_node_id ||
    checkboxState?.sourceNodeId === step.step_id_ref;

  // Get step_id_ref for comparison
  const stepIdRef = step.step_id_ref;

  // Show checkbox based on move type and source node
  // For source section's steps, show checkbox but disable it
  const shouldDisable = shouldDisableStepCheckbox(
    step.tree_node_id,
    stepIdRef,
    sectionId,
  );
  const showCheckbox =
    shouldShowStepCheckbox(step.tree_node_id, stepIdRef, sectionId) ||
    shouldDisable;
  const isDisabled =
    shouldDisable ||
    (moveNodeType === "step" && isSourceStep) ||
    (checkboxState?.allChildrenDisabled && !isCurrent) ||
    isSourceStep;

  // Source node's children should always be checked
  const isChecked =
    isCurrent ||
    checkboxState?.allChildrenChecked ||
    isSourceStep ||
    shouldDisable;

  return (
    <div key={step.tree_node_id} style={{ marginTop: "20px", clear: "both" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {showCheckbox && (
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled && !isSourceStep}
              onChange={() => {
                if (isSourceStep) return;
                if (!isDisabled && customOnNavigate) {
                  customOnNavigate("step", step, {
                    stepIndex,
                    sectionIndex,
                    sectionHeading: `Section ${sectionIndex + 1}`,
                    stepHeading,
                    globalStepNumber,
                    sectionId,
                    stepId: step.tree_node_id,
                  });
                }
              }}
              style={{
                cursor: isDisabled
                  ? "not-allowed"
                  : isSourceStep
                    ? "not-allowed"
                    : "pointer",
                accentColor: isSourceStep ? "black" : undefined,
              }}
            />
          )}
          <h2
            onClick={() => {
              if (isSourceStep) return;
              if (!isDisabled && showCheckbox && customOnNavigate) {
                customOnNavigate("step", step, {
                  stepIndex,
                  sectionIndex,
                  sectionHeading: `Section ${sectionIndex + 1}`,
                  stepHeading,
                  globalStepNumber,
                  sectionId,
                  stepId: step.tree_node_id,
                });
              }
            }}
            style={{
              fontSize: "20px",
              margin: 0,
              color:
                !isDisabled && showCheckbox
                  ? checkboxLabelColor || undefined
                  : undefined,
              cursor: isSourceStep
                ? "not-allowed"
                : !isDisabled && showCheckbox
                  ? "pointer"
                  : "default",
              userSelect: "none",
            }}
          >
            {stepHeading}
          </h2>
        </div>

        <div
          style={{
            color: "#888",
            fontSize: "14px",
            fontWeight: "normal",
          }}
        >
          Section {sectionIndex + 1} &gt; Step {globalStepNumber}
        </div>
      </div>
      <hr />
      <RenderHtml
        content={
          step.detail?.short_description || step.short_description || ""
        }
      />
      {/* Step Details */}
      {step.detail?.step_data?.step_details && (
        <p>
          {[...step.detail.step_data.step_details]
            .sort((a, b) => {
              const orderA = parseInt(a.orders || 0, 10);
              const orderB = parseInt(b.orders || 0, 10);
              if (orderA !== orderB) return orderA - orderB;
              const dateA = new Date(a.created_at || 0);
              const dateB = new Date(b.created_at || 0);
              return dateA - dateB;
            })
            .map((myStep, idx) => {
              const partNumber = myStep.part_info?.part_number || "";
              const description = myStep.part_info?.description || "";
              const qtyText =
                myStep.qty && myStep.qty !== 0 ? `Qty ${myStep.qty}` : "";
              const partText = partNumber ? ` (${partNumber})` : "";

              return (
                <span key={idx}>
                  {myStep.action}
                  {partText} <RenderHtml content={description} /> {qtyText}
                  &nbsp;
                </span>
              );
            })}
        </p>
      )}

      {/* BOM Table */}
      {step.detail?.step_data?.bom_table_title_info && (
        <h4>
          {step.detail.step_data.bom_table_title_info.category_name || ""}
        </h4>
      )}

      {step.detail?.step_data?.parts_required && (
        <PartsTable
          data={step.detail.step_data.parts_required}
          itemCodeColumnTitle={
            step.detail.step_data.part_number_column_title_ref_info
              ?.category_name || "Item Code"
          }
          dataFormat="step"
          autoSort={true}
          hideZeroQty={true}
        />
      )}

      {/* Render step children */}
      {step.children && step.children.length > 0 && (
        <>
          {[...step.children]
            .sort((a, b) => {
              const orderA = parseInt(a.sibling_order || 0, 10);
              const orderB = parseInt(b.sibling_order || 0, 10);
              return orderA - orderB;
            })
            .map((child, childIndex) => {
              const globalNodeNumber = calculateGlobalNodeNumberByType(
                child,
                child.type,
                preloadedData,
                compareSiblingOrders,
              );
              return (
                <MoveChildNode
                  key={child.tree_node_id}
                  child={child}
                  parentContext={{
                    sectionIndex,
                    stepIndex: globalStepNumber,
                    sectionId,
                    stepId: step.tree_node_id,
                    nodeIndex: childIndex,
                  }}
                  globalNodeNumber={globalNodeNumber || childIndex + 1}
                  sectionId={sectionId}
                  stepId={step.tree_node_id}
                  substepId={null}
                  preloadedData={preloadedData}
                  checkboxState={checkboxState}
                  moveNodeType={moveNodeType}
                  checkboxLabelColor={checkboxLabelColor}
                  customOnNavigate={customOnNavigate}
                  shouldDisableChildCheckbox={shouldDisableChildCheckbox}
                  shouldShowChildCheckbox={shouldShowChildCheckbox}
                />
              );
            })}
        </>
      )}

      {/* Render substeps */}
      {step.substeps && step.substeps.length > 0 && (
        <>
          {[...step.substeps]
            .sort((a, b) => {
              const orderA = parseInt(
                a.sibling_order || a.procedure_tree?.sibling_order || 0,
                10,
              );
              const orderB = parseInt(
                b.sibling_order || b.procedure_tree?.sibling_order || 0,
                10,
              );
              return orderA - orderB;
            })
            .map((substep, substepIndex) => (
              <MoveSubstepNode
                key={substep.tree_node_id}
                substep={substep}
                substepIndex={substepIndex}
                globalStepNumber={globalStepNumber}
                sectionIndex={sectionIndex}
                sectionId={sectionId}
                stepId={step.tree_node_id}
                preloadedData={preloadedData}
                checkboxState={checkboxState}
                moveNodeType={moveNodeType}
                checkboxLabelColor={checkboxLabelColor}
                customOnNavigate={customOnNavigate}
                shouldDisableSubstepCheckbox={shouldDisableSubstepCheckbox}
                shouldShowSubstepCheckbox={shouldShowSubstepCheckbox}
                shouldDisableChildCheckbox={shouldDisableChildCheckbox}
                shouldShowChildCheckbox={shouldShowChildCheckbox}
              />
            ))}
        </>
      )}
    </div>
  );
};

export default MoveStepNode;